import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from '../components/tooltip';
import { certifications } from '../routes/_index';

export const HavenAwayFromHomeSection = () => {
  const { t } = useTranslation(['home', 'common']);

  return (
    <section
      id="haven"
      className="py-20 md:py-28 bg-gradient-to-b from-green-100 via-white to-emerald-50"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
            {t('common:about-us.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-display text-emerald-900 mb-6">
            {t('home:haven.title')}
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            <Trans
              i18nKey="home:haven.premium-village"
              components={{
                link: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    className="text-emerald-600 font-semibold hover:text-emerald-700 underline underline-offset-2 decoration-emerald-300"
                    href="https://sukritwellness.com"
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
                bold: <strong className="text-emerald-800" />,
              }}
            />
          </p>
        </div>

        {/* Location & Dates Banner */}
        <div className="flex justify-center mb-8">
          <a
            href="https://www.google.com/maps/place/Kumbh+Sukrit+Camp/@25.3938104,81.9191783,477m/data=!3m1!1e3!4m6!3m5!1s0x39854bf48007e067:0x35f4c7d7b5e37e25!8m2!3d25.3941014!4d81.918992!16s%2Fg%2F11gjxvh252?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-3 px-5 py-2.5 bg-emerald-800 text-white rounded-full shadow-md hover:bg-emerald-700 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="text-sm font-medium">Sector 22, Prayagraj</span>
            <span className="text-emerald-300">•</span>
            <span className="text-sm font-medium text-emerald-200">
              31 Dec 2025 – End of Feb 2026
            </span>
          </a>
        </div>

        {/* Feature Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-emerald-100">
            <svg
              className="w-5 h-5 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              {t('common:features.safe-secure')}
            </span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-emerald-100">
            <svg
              className="w-5 h-5 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              {t('common:features.peaceful-serene')}
            </span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-emerald-100">
            <svg
              className="w-5 h-5 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              {t('common:features.clean-hygienic')}
            </span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-emerald-100">
            <svg
              className="w-5 h-5 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              {t('common:features.years-experience')}
            </span>
          </div>
        </div>

        {/* Certifications - Simple single line */}
        <div className="border-t border-emerald-100 pt-8">
          <p className="text-center text-xs text-gray-500 mb-4">
            {t('home:sw-certified')}
          </p>
          <ul className="flex items-center justify-center gap-6 md:gap-8 overflow-x-auto pb-2">
            {certifications.map(({ title, src, alt }, index) => (
              <li key={index} className="flex-shrink-0">
                <Tooltip text={title}>
                  <img
                    src={src}
                    alt={alt}
                    className="h-8 md:h-10 object-contain select-none cursor-pointer grayscale hover:grayscale-0 transition-all"
                  />
                </Tooltip>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
